import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScadFeature, FeatureField } from '@/lib/scad-feature-types';

interface ScadFeatureDialogProps {
  feature: ScadFeature | null;
  isOpen: boolean;
  initialValues?: Record<string, any>;
  isEditing?: boolean;
  onClose: () => void;
  onSubmit: (result: string | { before: string; after: string }) => void;
}

export function ScadFeatureDialog({
  feature,
  isOpen,
  initialValues,
  isEditing = false,
  onClose,
  onSubmit,
}: ScadFeatureDialogProps) {
  const [values, setValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (feature && feature.fields) {
      const initial: Record<string, any> = {};
      feature.fields.forEach((field) => {
        initial[field.name] = initialValues?.[field.name] ?? field.default;
      });
      setValues(initial);
    }
  }, [feature, initialValues]);

  if (!feature) return null;
  // Allow features with a diagram but no configurable fields to still render
  if (!feature.fields && !feature.diagram) return null;

  const handleChange = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleVectorChange = (name: string, index: number, value: string) => {
    const num = parseFloat(value);
    setValues((prev) => {
      const arr = [...(prev[name] || [0, 0, 0])];
      arr[index] = isNaN(num) ? 0 : num;
      return { ...prev, [name]: arr };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (feature.generateWrap) {
      onSubmit(feature.generateWrap(values));
    } else if (feature.generate) {
      onSubmit(feature.generate(values));
    } else if (feature.before !== undefined) {
      onSubmit({ before: feature.before, after: feature.after ?? '' });
    }
    onClose();
  };

  const Diagram = feature.diagram;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{feature.label}</DialogTitle>
            <DialogDescription>{feature.description}</DialogDescription>
          </DialogHeader>
          {Diagram && (
            <div className="w-full rounded-md overflow-hidden bg-muted/30 border border-border mb-1 mt-2">
              <Diagram values={values} />
            </div>
          )}
          <div className="grid gap-4 py-4">
            {(feature.fields ?? []).map((field) => (
              <div
                key={field.name}
                className="grid grid-cols-4 items-center gap-4"
              >
                <Label htmlFor={field.name} className="text-right">
                  {field.label}
                </Label>

                {field.type === 'number' && (
                  <Input
                    id={field.name}
                    type="number"
                    step={field.step ?? 1}
                    min={field.min}
                    max={field.max}
                    value={values[field.name] ?? ''}
                    onChange={(e) =>
                      handleChange(field.name, parseFloat(e.target.value))
                    }
                    className="col-span-3"
                  />
                )}

                {field.type === 'string' && (
                  <Input
                    id={field.name}
                    type="text"
                    value={values[field.name] ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    className="col-span-3"
                  />
                )}

                {field.type === 'boolean' && (
                  <div className="col-span-3 flex items-center space-x-2">
                    <Switch
                      id={field.name}
                      checked={values[field.name] === true}
                      onCheckedChange={(checked) =>
                        handleChange(field.name, checked)
                      }
                    />
                  </div>
                )}

                {field.type === 'vector2' && (
                  <div className="col-span-3 flex gap-2">
                    {['X', 'Y'].map((axis, i) => (
                      <Input
                        key={axis}
                        type="number"
                        step={field.step ?? 'any'}
                        placeholder={axis}
                        value={values[field.name]?.[i] ?? ''}
                        onChange={(e) =>
                          handleVectorChange(field.name, i, e.target.value)
                        }
                        className="w-full px-1 text-center"
                      />
                    ))}
                  </div>
                )}

                {field.type === 'vector3' && (
                  <div className="col-span-3 flex gap-2">
                    {['X', 'Y', 'Z'].map((axis, i) => (
                      <Input
                        key={axis}
                        type="number"
                        step={field.step ?? 'any'}
                        placeholder={axis}
                        value={values[field.name]?.[i] ?? ''}
                        onChange={(e) =>
                          handleVectorChange(field.name, i, e.target.value)
                        }
                        className="w-full px-1 text-center"
                      />
                    ))}
                  </div>
                )}

                {field.type === 'vector4' && (
                  <div className="col-span-3 flex gap-2">
                    {['R', 'G', 'B', 'A'].map((axis, i) => (
                      <Input
                        key={axis}
                        type="number"
                        step={field.step ?? 0.1}
                        placeholder={axis}
                        value={values[field.name]?.[i] ?? ''}
                        onChange={(e) =>
                          handleVectorChange(field.name, i, e.target.value)
                        }
                        className="w-full px-1 text-center"
                      />
                    ))}
                  </div>
                )}

                {field.type === 'select' && field.options && (
                  <div className="col-span-3">
                    <Select
                      value={String(values[field.name] ?? field.default)}
                      onValueChange={(val) => handleChange(field.name, val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <div className="flex w-full items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Press Enter to {isEditing ? 'update' : 'insert'}
              </span>
              <Button type="submit">{isEditing ? 'Update' : 'Insert'}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
